/**
 * Férias coletivas (M1.10/M1.11) — lista + detalhe: participantes com o split
 * dias debitados × recesso/licença remunerada (o 10+6 do caso real), checklist
 * de compliance documental (sindicato, MTE 15 dias — dispensado pela PREMISSA
 * EPP do dono 28/07/2026, D26, com ressalva; cópia ao sindicato; afixação;
 * ata/convenção) e cadastro MANUAL (fallback da API — dado cadastral datado,
 * não ajusta períodos aquisitivos). Só orienta — não valida documentos.
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { IconAlertTriangle, IconPencil, IconPlus, IconTrash, IconUsersGroup } from "@tabler/icons-react";
import { api } from "../api";
import { navegar } from "../roteador";
import { fmtData } from "../format";
import { ItemEntidade, TabelaDados, Th, TdNum } from "../componentes/listas";
import { DataField } from "../componentes/entradas-mantine";
import { Confirmacao, ErroApiAlert, notificarErroApi, notificarOk } from "../componentes/notificacoes";

interface ColetivaResumo {
  id: string;
  titulo: string;
  inicio: string;
  fim: string;
  dias: number;
  descontarFeriado: boolean;
  justificativa: string | null;
  origem: string;
  sindicato: string | null;
  mteStatus: string;
  copiaSindicato: boolean;
  afixacao: boolean;
  ataConvencao: boolean;
  complianceNotas: string | null;
  participantes: number;
}

interface Participante {
  id: string;
  funcionarioId: string;
  nome: string;
  tipoVinculo: string;
  diasDebitados: number;
  diasRecesso: number;
  periodoAquisitivoId: string | null;
  periodoAquisitivo: { inicio: string; fim: string | null } | null;
  solicitacaoId: string | null;
  origem: string;
}

interface SolicitacaoColetiva {
  id: string;
  nome: string;
  inicioGozo: string;
  diasGozo: number;
  consumoDias: number | null;
  paidLeaveDias: number;
  status: string;
}

interface Detalhe {
  coletiva: ColetivaResumo;
  participantes: Participante[];
  solicitacoes: SolicitacaoColetiva[];
  alertas: string[];
}

interface FuncionarioOpcao {
  id: string;
  nome: string;
  tipoVinculo: string;
}

interface PeriodoOpcao {
  id: string;
  inicioAquisitivo: string;
  fimAquisitivo: string;
}

const MTE_OPCOES = [
  { value: "dispensada_epp", label: "Dispensada — EPP (premissa, LC 123 art. 51, V)" },
  { value: "pendente", label: "Pendente" },
  { value: "comunicada", label: "Comunicada ao MTE" },
];

export function FeriasColetivasPage({ id }: { id?: string }) {
  return id ? <DetalheColetiva id={id} /> : <ListaColetivas />;
}

// ---------------------------------------------------------------------------
// Lista
// ---------------------------------------------------------------------------

function ListaColetivas() {
  const [lista, setLista] = useState<ColetivaResumo[]>([]);
  const [erro, setErro] = useState<unknown>(null);
  const [carregando, setCarregando] = useState(true);
  const [novaAberta, setNovaAberta] = useState(false);

  const carregar = useCallback(async () => {
    const r = await api.get<{ coletivas: ColetivaResumo[] }>("/ferias-coletivas");
    setLista(r.coletivas);
  }, []);

  useEffect(() => {
    setCarregando(true);
    carregar()
      .catch(setErro)
      .finally(() => setCarregando(false));
  }, [carregar]);

  return (
    <>
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={2}>Férias coletivas</Title>
          <Text size="sm" c="dimmed">
            Coletivas espelhadas do Convenia + cadastro manual, com o split por participante (dias de férias × recesso)
            e o checklist de compliance (sindicato, MTE, afixação).
          </Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setNovaAberta(true)}>
          Nova coletiva manual
        </Button>
      </Group>
      <ErroApiAlert erro={erro} />
      {carregando ? (
        <Text c="dimmed" size="sm">Carregando…</Text>
      ) : lista.length === 0 ? (
        <Text c="dimmed" size="sm">
          Nenhuma coletiva ainda — sincronize o domínio “Férias coletivas” na integração Convenia ou cadastre manualmente.
        </Text>
      ) : (
        lista.map((c) => (
          <ItemEntidade
            key={c.id}
            icone={
              <ThemeIcon variant="light" color="orange" size="lg">
                <IconUsersGroup size={18} />
              </ThemeIcon>
            }
            titulo={c.titulo}
            linhaSecundaria={`${fmtData(c.inicio)} a ${fmtData(c.fim)} · ${c.dias} dias · ${c.participantes} participante(s)`}
            badges={
              <>
                <Badge variant="light" color={c.origem === "manual" ? "grape" : "sillis"}>
                  {c.origem === "manual" ? "manual" : "Convenia"}
                </Badge>
                {c.sindicato && <Badge variant="light" color="gray">{c.sindicato}</Badge>}
                {c.mteStatus === "pendente" && <Badge variant="light" color="red">MTE pendente</Badge>}
              </>
            }
            onClick={() => navegar(`/ferias-coletivas/${c.id}`)}
            ariaLabel={`Abrir coletiva ${c.titulo}`}
          />
        ))
      )}
      <ModalNovaColetiva aberta={novaAberta} onFechar={() => setNovaAberta(false)} onCriada={(id) => navegar(`/ferias-coletivas/${id}`)} />
    </>
  );
}

function ModalNovaColetiva({ aberta, onFechar, onCriada }: { aberta: boolean; onFechar: () => void; onCriada: (id: string) => void }) {
  const [titulo, setTitulo] = useState("");
  const [inicio, setInicio] = useState<string | null>(null);
  const [fim, setFim] = useState<string | null>(null);
  const [descontarFeriado, setDescontarFeriado] = useState(false);
  const [justificativa, setJustificativa] = useState("");
  const [sindicato, setSindicato] = useState("");
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  async function criar() {
    setSalvando(true);
    setErro(null);
    try {
      const r = await api.post<{ id: string; alertas: string[] }>("/ferias-coletivas", {
        titulo,
        inicio,
        fim,
        descontarFeriado,
        justificativa: justificativa.trim() || null,
        sindicato: sindicato.trim() || null,
      });
      notificarOk(
        r.alertas.length > 0 ? `Coletiva criada. Atenção: ${r.alertas.join(" ")}` : "Coletiva manual criada — agora inclua os participantes.",
      );
      onCriada(r.id);
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setSalvando(false);
    }
  }

  const pronta = titulo.trim() !== "" && inicio !== null && fim !== null;
  return (
    <Modal opened={aberta} onClose={onFechar} title="Nova coletiva manual" centered size="lg">
      <Text size="sm" c="dimmed" mb="sm">
        Fallback da API (o Convenia não expõe participantes nem sindicato) e registro de coletivas fora do Convenia. O
        cadastro NÃO muda períodos aquisitivos — eles já vêm certos do sync.
      </Text>
      <ErroApiAlert erro={erro} />
      <TextInput label="Título" value={titulo} onChange={(e) => setTitulo(e.currentTarget.value)} mb="sm" placeholder="Ex.: Férias Coletivas 2026/2027" />
      <Group grow mb="sm">
        <DataField label="Início" valor={inicio} onChange={setInicio} />
        <DataField label="Fim" valor={fim} onChange={setFim} />
      </Group>
      <Group grow mb="sm">
        <TextInput label="Sindicato (opcional)" value={sindicato} onChange={(e) => setSindicato(e.currentTarget.value)} placeholder="Ex.: SINTETEL-SP" />
        <Checkbox
          label="Descontar feriado do saldo (convenção do Convenia — o default legal é feriado CONSUMIR saldo)"
          checked={descontarFeriado}
          onChange={(e) => setDescontarFeriado(e.currentTarget.checked)}
          mt="xl"
        />
      </Group>
      <Textarea label="Justificativa (opcional)" value={justificativa} onChange={(e) => setJustificativa(e.currentTarget.value)} mb="md" minRows={2} />
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>Voltar</Button>
        <Button onClick={() => void criar()} disabled={!pronta} loading={salvando}>Criar coletiva</Button>
      </Group>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Detalhe
// ---------------------------------------------------------------------------

function DetalheColetiva({ id }: { id: string }) {
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [editando, setEditando] = useState<Participante | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [removendo, setRemovendo] = useState<Participante | null>(null);

  const carregar = useCallback(async () => {
    const r = await api.get<Detalhe>(`/ferias-coletivas/${id}`);
    setDetalhe(r);
  }, [id]);

  useEffect(() => {
    carregar().catch(setErro);
  }, [carregar]);

  async function remover(p: Participante) {
    try {
      await api.del(`/ferias-coletivas/${id}/participantes/${p.id}`);
      notificarOk(`Participação de ${p.nome} removida.`);
      setRemovendo(null);
      await carregar();
    } catch (e) {
      notificarErroApi(e);
      setRemovendo(null);
    }
  }

  if (erro) return <ErroApiAlert erro={erro} />;
  if (!detalhe) return <Text c="dimmed" size="sm">Carregando…</Text>;
  const c = detalhe.coletiva;

  return (
    <>
      <Group justify="space-between" mb="xs" wrap="wrap">
        <div>
          <Title order={2}>{c.titulo}</Title>
          <Text size="sm" c="dimmed">
            {fmtData(c.inicio)} a {fmtData(c.fim)} · {c.dias} dias ·{" "}
            {c.origem === "manual" ? "cadastro manual" : "espelho do Convenia"}
            {c.justificativa ? ` · ${c.justificativa}` : ""}
          </Text>
        </div>
        <Button variant="default" onClick={() => navegar("/ferias-coletivas")}>Voltar à lista</Button>
      </Group>

      {detalhe.alertas.length > 0 && (
        <Alert color="yellow" icon={<IconAlertTriangle size={16} />} mb="md">
          {detalhe.alertas.map((a, i) => (
            <Text key={i} size="sm">{a}</Text>
          ))}
        </Alert>
      )}

      <ComplianceCard coletiva={c} onSalva={carregar} />

      <Group justify="space-between" mt="lg" mb="xs">
        <Title order={4}>Participantes ({detalhe.participantes.length})</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdicionando(true)}>
          Adicionar participante
        </Button>
      </Group>
      {detalhe.participantes.length === 0 ? (
        <Text c="dimmed" size="sm" mb="md">
          Nenhum participante — o sync deriva das solicitações vinculadas; para coletiva manual, cadastre aqui (o split
          alimenta o saldo derivado: só “dias de férias” consomem saldo).
        </Text>
      ) : (
        <TabelaDados>
          <thead>
            <tr>
              <Th>Pessoa</Th>
              <Th>Vínculo</Th>
              <Th>Período aquisitivo</Th>
              <Th num>Férias (debita)</Th>
              <Th num>Recesso (não debita)</Th>
              <Th>Origem</Th>
              <Th>{""}</Th>
            </tr>
          </thead>
          <tbody>
            {detalhe.participantes.map((p) => (
              <tr key={p.id}>
                <td>{p.nome}</td>
                <td>
                  <Badge variant="light" color={p.tipoVinculo === "socio" ? "grape" : "sillis"}>
                    {p.tipoVinculo === "socio" ? "Sócio (informativo)" : "CLT"}
                  </Badge>
                </td>
                <td>
                  {p.periodoAquisitivo
                    ? `${fmtData(p.periodoAquisitivo.inicio)} a ${p.periodoAquisitivo.fim ? fmtData(p.periodoAquisitivo.fim) : "—"}`
                    : "—"}
                </td>
                <TdNum>{p.diasDebitados}</TdNum>
                <TdNum>{p.diasRecesso}</TdNum>
                <td>
                  <Badge variant="light" color={p.origem === "manual" ? "grape" : "gray"}>
                    {p.origem === "manual" ? "manual" : "derivada do sync"}
                  </Badge>
                </td>
                <td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <Button size="compact-xs" variant="subtle" leftSection={<IconPencil size={13} />} onClick={() => setEditando(p)}>
                      Editar
                    </Button>
                    {p.origem === "manual" && (
                      <Button size="compact-xs" variant="subtle" color="red" leftSection={<IconTrash size={13} />} onClick={() => setRemovendo(p)}>
                        Remover
                      </Button>
                    )}
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </TabelaDados>
      )}

      {detalhe.solicitacoes.length > 0 && (
        <>
          <Title order={4} mt="lg" mb="xs">Solicitações vinculadas (Convenia)</Title>
          <TabelaDados>
            <thead>
              <tr>
                <Th>Pessoa</Th>
                <Th>Início do gozo</Th>
                <Th num>Dias</Th>
                <Th num>Licença remunerada</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {detalhe.solicitacoes.map((s) => (
                <tr key={s.id}>
                  <td>{s.nome}</td>
                  <td>{fmtData(s.inicioGozo)}</td>
                  <TdNum>{s.consumoDias ?? s.diasGozo}</TdNum>
                  <TdNum>{s.paidLeaveDias}</TdNum>
                  <td>{s.status}</td>
                </tr>
              ))}
            </tbody>
          </TabelaDados>
        </>
      )}

      <ModalParticipante
        coletivaId={id}
        diasColetiva={c.dias}
        solicitacoes={detalhe.solicitacoes}
        participante={editando}
        aberta={adicionando || editando !== null}
        onFechar={() => {
          setAdicionando(false);
          setEditando(null);
        }}
        onSalva={carregar}
      />
      <Confirmacao
        aberta={removendo !== null}
        titulo={`Remover ${removendo?.nome ?? ""} da coletiva?`}
        consequencia="A participação manual será apagada (fica na auditoria). O saldo derivado dessa pessoa volta a cair no spent_balance − licença remunerada da solicitação."
        rotuloConfirmar="Remover"
        onConfirmar={() => removendo && void remover(removendo)}
        onCancelar={() => setRemovendo(null)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Compliance documental (M1.11 — informativo: exibe/orienta, não valida)
// ---------------------------------------------------------------------------

function ComplianceCard({ coletiva: c, onSalva }: { coletiva: ColetivaResumo; onSalva: () => Promise<void> }) {
  const [sindicato, setSindicato] = useState(c.sindicato ?? "");
  const [mteStatus, setMteStatus] = useState(c.mteStatus);
  const [copiaSindicato, setCopiaSindicato] = useState(c.copiaSindicato);
  const [afixacao, setAfixacao] = useState(c.afixacao);
  const [ataConvencao, setAtaConvencao] = useState(c.ataConvencao);
  const [notas, setNotas] = useState(c.complianceNotas ?? "");
  const [salvando, setSalvando] = useState(false);

  async function salvar() {
    setSalvando(true);
    try {
      const r = await api.patch<{ ok: boolean; alertas: string[] }>(`/ferias-coletivas/${c.id}`, {
        sindicato: sindicato.trim() || null,
        mteStatus,
        copiaSindicato,
        afixacao,
        ataConvencao,
        complianceNotas: notas.trim() || null,
      });
      notificarOk(r.alertas.length > 0 ? `Compliance salvo. Atenção: ${r.alertas.join(" ")}` : "Compliance salvo.");
      await onSalva();
    } catch (e) {
      notificarErroApi(e);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Alert color="gray" title="Compliance documental (informativo — o sistema orienta, não valida documentos)" mb="md">
      <Text size="xs" c="dimmed" mb="sm">
        Comunicação ao MTE 15 dias antes: <strong>dispensada para EPP</strong> (premissa do dono 28/07/2026 — SilliS Ltda
        lucro presumido, enquadramento a revalidar pela receita bruta anual, LC 123 art. 51, V). Cópia ao sindicato,
        afixação de aviso e ata/convenção (quando houver abono coletivo) seguem no checklist.
      </Text>
      <Group grow mb="sm" align="flex-end">
        <TextInput label="Sindicato" value={sindicato} onChange={(e) => setSindicato(e.currentTarget.value)} placeholder="Ex.: SINTETEL-SP" />
        <Select label="Comunicação ao MTE (15 dias)" data={MTE_OPCOES} value={mteStatus} onChange={(v) => v && setMteStatus(v)} />
      </Group>
      <Group mb="sm">
        <Checkbox label="Cópia enviada ao sindicato" checked={copiaSindicato} onChange={(e) => setCopiaSindicato(e.currentTarget.checked)} />
        <Checkbox label="Aviso afixado no local de trabalho" checked={afixacao} onChange={(e) => setAfixacao(e.currentTarget.checked)} />
        <Checkbox label="Ata/convenção (abono coletivo)" checked={ataConvencao} onChange={(e) => setAtaConvencao(e.currentTarget.checked)} />
      </Group>
      <Textarea label="Notas de compliance" value={notas} onChange={(e) => setNotas(e.currentTarget.value)} minRows={2} mb="sm" />
      <Group justify="flex-end">
        <Button size="xs" onClick={() => void salvar()} loading={salvando}>Salvar compliance</Button>
      </Group>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Participante — adicionar / editar split
// ---------------------------------------------------------------------------

function ModalParticipante({
  coletivaId,
  diasColetiva,
  solicitacoes,
  participante,
  aberta,
  onFechar,
  onSalva,
}: {
  coletivaId: string;
  diasColetiva: number;
  solicitacoes: SolicitacaoColetiva[];
  participante: Participante | null; // null = novo
  aberta: boolean;
  onFechar: () => void;
  onSalva: () => Promise<void>;
}) {
  const [funcionarios, setFuncionarios] = useState<FuncionarioOpcao[]>([]);
  const [periodos, setPeriodos] = useState<PeriodoOpcao[]>([]);
  const [funcionarioId, setFuncionarioId] = useState<string | null>(null);
  const [diasDebitados, setDiasDebitados] = useState<number | "">("");
  const [diasRecesso, setDiasRecesso] = useState<number | "">(0);
  const [periodoAquisitivoId, setPeriodoAquisitivoId] = useState<string | null>(null);
  const [solicitacaoId, setSolicitacaoId] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!aberta) return;
    api
      .get<{ funcionarios: FuncionarioOpcao[] }>("/funcionarios?ativo=true")
      .then((r) => setFuncionarios(r.funcionarios))
      .catch(() => undefined);
  }, [aberta]);

  useEffect(() => {
    setFuncionarioId(participante?.funcionarioId ?? null);
    setDiasDebitados(participante?.diasDebitados ?? "");
    setDiasRecesso(participante?.diasRecesso ?? 0);
    setPeriodoAquisitivoId(participante?.periodoAquisitivoId ?? null);
    setSolicitacaoId(participante?.solicitacaoId ?? null);
    setErro(null);
  }, [participante, aberta]);

  useEffect(() => {
    if (!funcionarioId) {
      setPeriodos([]);
      return;
    }
    api
      .get<{ periodos: PeriodoOpcao[] }>(`/funcionarios/${funcionarioId}/ferias-estoque`)
      .then((r) => setPeriodos(Array.isArray(r?.periodos) ? r.periodos : []))
      .catch(() => setPeriodos([]));
  }, [funcionarioId]);

  async function salvar() {
    setSalvando(true);
    setErro(null);
    try {
      const corpo = {
        funcionarioId,
        diasDebitados,
        diasRecesso: diasRecesso === "" ? 0 : diasRecesso,
        periodoAquisitivoId,
        solicitacaoId,
      };
      const r = participante
        ? await api.patch<{ alertas: string[] }>(`/ferias-coletivas/${coletivaId}/participantes/${participante.id}`, corpo)
        : await api.post<{ alertas: string[] }>(`/ferias-coletivas/${coletivaId}/participantes`, corpo);
      notificarOk(
        r.alertas.length > 0
          ? `Participação salva. Atenção: ${r.alertas.join(" ")}`
          : participante
            ? "Participação atualizada — virou manual (preservada no próximo sync)."
            : "Participante adicionado.",
      );
      onFechar();
      await onSalva();
    } catch (e) {
      setErro(e);
      notificarErroApi(e);
    } finally {
      setSalvando(false);
    }
  }

  const split = (diasDebitados === "" ? 0 : diasDebitados) + (diasRecesso === "" ? 0 : diasRecesso);
  const pronto =
    (participante !== null || funcionarioId !== null) && diasDebitados !== "" && diasDebitados >= 0 && (diasRecesso === "" || diasRecesso >= 0);

  return (
    <Modal opened={aberta} onClose={onFechar} title={participante ? `Editar participação — ${participante.nome}` : "Adicionar participante"} centered size="lg">
      <Text size="sm" c="dimmed" mb="sm">
        Split da coletiva: <strong>dias de férias</strong> debitam o saldo; <strong>dias de recesso</strong> (licença
        remunerada) são pagos como salário e não debitam. Caso real 2021/2022: proporcionais 10+6, período completo 16+0.
      </Text>
      <ErroApiAlert erro={erro} />
      {!participante && (
        <Select
          label="Pessoa"
          placeholder="Selecione"
          data={funcionarios.map((f) => ({ value: f.id, label: `${f.nome}${f.tipoVinculo === "socio" ? " (sócio — informativo)" : ""}` }))}
          value={funcionarioId}
          onChange={setFuncionarioId}
          searchable
          mb="sm"
        />
      )}
      <Group grow mb="sm">
        <NumberInput label="Dias de férias (debita saldo)" min={0} max={30} value={diasDebitados} onChange={(v) => setDiasDebitados(v === "" ? "" : Number(v))} />
        <NumberInput label="Dias de recesso (não debita)" min={0} max={30} value={diasRecesso} onChange={(v) => setDiasRecesso(v === "" ? "" : Number(v))} />
      </Group>
      {split !== diasColetiva && (
        <Text size="xs" c="orange" mb="sm">
          O split ({split}) difere dos dias da coletiva ({diasColetiva}) — a API aceita com alerta; confira.
        </Text>
      )}
      <Group grow mb="md">
        <Select
          label="Período aquisitivo (opcional)"
          placeholder="Sem vínculo"
          clearable
          data={periodos.map((p) => ({ value: p.id, label: `${fmtData(p.inicioAquisitivo)} a ${fmtData(p.fimAquisitivo)}` }))}
          value={periodoAquisitivoId}
          onChange={setPeriodoAquisitivoId}
        />
        <Select
          label="Solicitação vinculada (opcional)"
          placeholder="Sem vínculo"
          clearable
          data={solicitacoes
            .filter((s) => !funcionarioId || s.nome === funcionarios.find((f) => f.id === funcionarioId)?.nome || participante?.nome === s.nome)
            .map((s) => ({ value: s.id, label: `${s.nome} — ${fmtData(s.inicioGozo)} (${s.consumoDias ?? s.diasGozo}d)` }))}
          value={solicitacaoId}
          onChange={setSolicitacaoId}
        />
      </Group>
      <Group justify="flex-end">
        <Button variant="default" onClick={onFechar}>Voltar</Button>
        <Button onClick={() => void salvar()} disabled={!pronto} loading={salvando}>
          {participante ? "Salvar" : "Adicionar"}
        </Button>
      </Group>
    </Modal>
  );
}
